import { createSvgEl } from '@learnforge/demo-render';

export type AlgebraTileKind = 'x2' | 'x' | 'unit';

export interface AlgebraTile {
  id: string;
  kind: AlgebraTileKind;
  sign: 1 | -1;
  position: { col: number; row: number };
  orientation: 'horizontal' | 'vertical';
}

export interface AlgebraTilesOptions {
  tiles: readonly AlgebraTile[];
  workspaceCols: number;
  workspaceRows: number;
  width: number;
  height: number;
  onTileMove?(id: string, position: { col: number; row: number }): void;
  onArrangementValid?(rectangle: {
    width: number;
    height: number;
    sideLabels: [string, string];
  }): void;
}

export interface AlgebraTilesHandle {
  group: SVGGElement;
  update(opts: Partial<AlgebraTilesOptions>): void;
  destroy(): void;
}

const GRID_CELL_SIZE = 20;

function getTileDimensions(
  kind: AlgebraTileKind,
  orientation: 'horizontal' | 'vertical',
): { cols: number; rows: number } {
  if (kind === 'unit') {
    return { cols: 1, rows: 1 };
  }
  if (kind === 'x') {
    return orientation === 'horizontal' ? { cols: 3, rows: 1 } : { cols: 1, rows: 3 };
  }
  if (kind === 'x2') {
    return { cols: 3, rows: 3 };
  }
  const exhaustive: never = kind;
  return exhaustive;
}

function getTileClass(kind: AlgebraTileKind, sign: 1 | -1): string {
  if (sign === -1) {
    return 'alg-tile-neg';
  }
  switch (kind) {
    case 'unit':
      return 'alg-tile-unit';
    case 'x':
      return 'alg-tile-x';
    case 'x2':
      return 'alg-tile-x2';
  }
}

function getTileLabel(kind: AlgebraTileKind): string {
  switch (kind) {
    case 'unit':
      return '1';
    case 'x':
      return 'x';
    case 'x2':
      return 'x²';
  }
}

function renderTile(tile: AlgebraTile): SVGGElement {
  const g = createSvgEl('g', {
    'data-tile-id': tile.id,
    'data-kind': tile.kind,
  });

  const dims = getTileDimensions(tile.kind, tile.orientation);
  const widthPx = dims.cols * GRID_CELL_SIZE;
  const heightPx = dims.rows * GRID_CELL_SIZE;

  const tileClass = getTileClass(tile.kind, tile.sign);

  const rect = createSvgEl('rect', {
    x: 0,
    y: 0,
    width: widthPx,
    height: heightPx,
    class: tileClass,
  });
  g.appendChild(rect);

  // Add label centered on the tile
  const label = createSvgEl('text', {
    class: 'alg-tile-label',
    x: widthPx / 2,
    y: heightPx / 2,
    'text-anchor': 'middle',
    'dominant-baseline': 'central',
  });
  label.textContent = getTileLabel(tile.kind);
  g.appendChild(label);

  return g;
}

function getTileBounds(tile: AlgebraTile): {
  minCol: number;
  maxCol: number;
  minRow: number;
  maxRow: number;
} {
  const dims = getTileDimensions(tile.kind, tile.orientation);
  return {
    minCol: tile.position.col,
    maxCol: tile.position.col + dims.cols - 1,
    minRow: tile.position.row,
    maxRow: tile.position.row + dims.rows - 1,
  };
}

function isValidRectangleArrangement(
  tiles: readonly AlgebraTile[],
):
  | { valid: boolean; width: number; height: number; sideLabels: [string, string] }
  | { valid: false } {
  if (tiles.length === 0) {
    return { valid: false };
  }

  // Compute the bounding box of all tiles
  let minCol = Infinity;
  let maxCol = -Infinity;
  let minRow = Infinity;
  let maxRow = -Infinity;

  const tileBounds = tiles.map((tile) => getTileBounds(tile));

  for (const bounds of tileBounds) {
    minCol = Math.min(minCol, bounds.minCol);
    maxCol = Math.max(maxCol, bounds.maxCol);
    minRow = Math.min(minRow, bounds.minRow);
    maxRow = Math.max(maxRow, bounds.maxRow);
  }

  const boundingWidth = maxCol - minCol + 1;
  const boundingHeight = maxRow - minRow + 1;

  // Count total cells covered by tiles
  const cellCoverage = Array.from({ length: boundingHeight }, () =>
    Array.from({ length: boundingWidth }, () => 0),
  );

  for (const tile of tiles) {
    const dims = getTileDimensions(tile.kind, tile.orientation);
    for (let r = 0; r < dims.rows; r++) {
      for (let c = 0; c < dims.cols; c++) {
        const cellRow = tile.position.row - minRow + r;
        const cellCol = tile.position.col - minCol + c;
        if (cellRow < 0 || cellRow >= boundingHeight || cellCol < 0 || cellCol >= boundingWidth) {
          return { valid: false };
        }
        cellCoverage[cellRow]![cellCol]! += 1;
      }
    }
  }

  // Check that every cell is covered exactly once
  for (let r = 0; r < boundingHeight; r++) {
    for (let c = 0; c < boundingWidth; c++) {
      if (cellCoverage[r]![c] !== 1) {
        return { valid: false };
      }
    }
  }

  // Determine side labels by analyzing the dimensions of tiles on each edge.
  // For HEIGHT: look at the span of each unique row-contiguous group of tiles.
  // For WIDTH: look at the span of each unique col-contiguous group of tiles.

  // Analyze HEIGHT by looking at tiles on the left edge.
  const leftEdgeTiles = tiles.filter((t) => getTileBounds(t).minCol === minCol);

  let heightXCount = 0;
  let heightUnitCount = 0;

  for (const tile of leftEdgeTiles) {
    const dims = getTileDimensions(tile.kind, tile.orientation);
    if (dims.rows === 3) {
      heightXCount += 1;
    } else if (dims.rows === 1) {
      heightUnitCount += 1;
    }
  }

  // Analyze WIDTH by looking at tiles on the top edge, grouped by their col extent
  const topEdgeTiles = tiles.filter((t) => getTileBounds(t).minRow === minRow);

  let widthXCount = 0;
  let widthUnitCount = 0;

  for (const tile of topEdgeTiles) {
    const dims = getTileDimensions(tile.kind, tile.orientation);
    if (dims.cols === 3) {
      widthXCount += 1;
    } else if (dims.cols === 1) {
      widthUnitCount += 1;
    }
  }

  // Build side label strings
  const buildLabel = (xCount: number, unitCount: number): string => {
    if (xCount === 0) {
      return unitCount.toString();
    }
    const xPart = xCount === 1 ? 'x' : `${xCount}x`;
    if (unitCount === 0) {
      return xPart;
    }
    return `${xPart} + ${unitCount}`;
  };

  const leftLabel = buildLabel(heightXCount, heightUnitCount);
  const topLabel = buildLabel(widthXCount, widthUnitCount);

  return {
    valid: true,
    width: boundingWidth * GRID_CELL_SIZE,
    height: boundingHeight * GRID_CELL_SIZE,
    sideLabels: [leftLabel, topLabel],
  };
}

export function createAlgebraTiles(
  parent: SVGSVGElement | SVGGElement,
  opts: AlgebraTilesOptions,
): AlgebraTilesHandle {
  const group = createSvgEl('g');
  parent.appendChild(group);

  const currentOpts = {
    tiles: Array.from(opts.tiles),
    workspaceCols: opts.workspaceCols,
    workspaceRows: opts.workspaceRows,
    width: opts.width,
    height: opts.height,
    onTileMove: opts.onTileMove
      ? (id: string, pos: { col: number; row: number }) => opts.onTileMove!(id, pos)
      : undefined,
    onArrangementValid: opts.onArrangementValid
      ? (rect: { width: number; height: number; sideLabels: [string, string] }) =>
          opts.onArrangementValid!(rect)
      : undefined,
  };

  let dragState: {
    tileId: string;
    startCol: number;
    startRow: number;
    startX: number;
    startY: number;
  } | null = null;

  function renderTiles(): void {
    // Clear existing tiles
    while (group.firstChild) {
      group.removeChild(group.firstChild);
    }

    // Render each tile
    for (const tile of currentOpts.tiles) {
      const tileEl = renderTile(tile);
      const x = tile.position.col * GRID_CELL_SIZE;
      const y = tile.position.row * GRID_CELL_SIZE;
      tileEl.setAttribute('transform', `translate(${x},${y})`);

      // Attach drag handlers
      tileEl.addEventListener('pointerdown', (e) => handlePointerDown(tile.id)(e));

      group.appendChild(tileEl);
    }
  }

  function handlePointerDown(tileId: string) {
    return (e: Event) => {
      const pointerEvent = e as PointerEvent;
      const tile = currentOpts.tiles.find((t) => t.id === tileId);
      if (!tile) return;

      dragState = {
        tileId,
        startCol: tile.position.col,
        startRow: tile.position.row,
        startX: pointerEvent.clientX,
        startY: pointerEvent.clientY,
      };

      document.addEventListener('pointermove', () => handlePointerMove());
      document.addEventListener('pointerup', (e) => handlePointerUp(e));
    };
  }

  function handlePointerMove() {
    if (!dragState) return;
    // During drag, we just track movement; actual update happens on pointerup.
  }

  function handlePointerUp(e: Event) {
    if (!dragState) return;

    const pointerEvent = e as PointerEvent;
    const deltaX = pointerEvent.clientX - dragState.startX;
    const deltaY = pointerEvent.clientY - dragState.startY;

    // Snap to grid: compute new col/row based on delta
    const deltaCol = Math.round(deltaX / GRID_CELL_SIZE);
    const deltaRow = Math.round(deltaY / GRID_CELL_SIZE);

    const newCol = dragState.startCol + deltaCol;
    const newRow = dragState.startRow + deltaRow;

    // Clamp to workspace bounds
    const tile = currentOpts.tiles.find((t) => t.id === dragState!.tileId);
    if (tile) {
      const dims = getTileDimensions(tile.kind, tile.orientation);
      const clampedCol = Math.max(0, Math.min(newCol, currentOpts.workspaceCols - dims.cols));
      const clampedRow = Math.max(0, Math.min(newRow, currentOpts.workspaceRows - dims.rows));

      // Update tile position
      const tileIndex = currentOpts.tiles.findIndex((t) => t.id === dragState!.tileId);
      if (tileIndex !== -1) {
        const updatedTile: AlgebraTile = {
          ...tile,
          position: { col: clampedCol, row: clampedRow },
        };
        currentOpts.tiles = [
          ...currentOpts.tiles.slice(0, tileIndex),
          updatedTile,
          ...currentOpts.tiles.slice(tileIndex + 1),
        ];

        // Re-render and call callback
        renderTiles();
        checkArrangement();
        currentOpts.onTileMove?.(dragState.tileId, { col: clampedCol, row: clampedRow });
      }
    }

    dragState = null;
  }

  function checkArrangement(): void {
    const result = isValidRectangleArrangement(currentOpts.tiles);
    if (result.valid) {
      currentOpts.onArrangementValid?.({
        width: result.width,
        height: result.height,
        sideLabels: result.sideLabels,
      });
    }
  }

  renderTiles();
  checkArrangement();

  const handle: AlgebraTilesHandle = {
    group,

    update(partial: Partial<AlgebraTilesOptions>): void {
      if (partial.tiles !== undefined) {
        currentOpts.tiles = Array.from(partial.tiles);
      }
      if (partial.workspaceCols !== undefined) {
        currentOpts.workspaceCols = partial.workspaceCols;
      }
      if (partial.workspaceRows !== undefined) {
        currentOpts.workspaceRows = partial.workspaceRows;
      }
      if (partial.width !== undefined) {
        currentOpts.width = partial.width;
      }
      if (partial.height !== undefined) {
        currentOpts.height = partial.height;
      }
      if (partial.onTileMove !== undefined) {
        currentOpts.onTileMove = partial.onTileMove;
      }
      if (partial.onArrangementValid !== undefined) {
        currentOpts.onArrangementValid = partial.onArrangementValid;
      }
      renderTiles();
      checkArrangement();
    },

    destroy(): void {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      if (group.parentNode) {
        group.parentNode.removeChild(group);
      }
    },
  };

  return handle;
}
